/**
 * Generic factory for per-user preference settings stored in profiles.preferences JSON.
 * Shared by useDateFormatSettings and useTimeFormatSettings to avoid duplication.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { getLogoutEpoch } from '@/lib/logout-teardown-signal';
import { isSignedOutReadFailure } from '@/lib/post-logout-denial';
import { useAuth } from '@/contexts/auth-context';

// =============================================================================
// Factory
// =============================================================================

interface UseProfilePreferenceOptions<T extends string> {
  /** The key inside the profiles.preferences JSON object (e.g., 'date_format', 'time_format') */
  preferenceKey: string;
  /** TanStack Query key prefix (e.g., 'date-format-settings') */
  queryKeyPrefix: string;
  /** Default value when no preference is set */
  defaultValue: T;
  /** Valid values for runtime validation in the mutation */
  validValues: ReadonlySet<T>;
}

/**
 * Hook to fetch a single preference value from profiles.preferences.
 * Priority: user's profile → org-level settings → default.
 */
export function useProfilePreference<T extends string>(
  organizationId: string | null | undefined,
  options: UseProfilePreferenceOptions<T>,
): T {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { preferenceKey, queryKeyPrefix, defaultValue } = options;

  const { data } = useQuery({
    queryKey: [queryKeyPrefix, userId, organizationId],
    placeholderData: () => {
      const profile = queryClient.getQueryData<{ preferences?: Record<string, unknown> }>([
        'current-org-profile',
        userId,
        organizationId,
      ]);
      return (profile?.preferences?.[preferenceKey] as T) ?? null;
    },
    queryFn: async () => {
      if (!organizationId || !userId) {
        return null;
      }

      const logoutEpoch = getLogoutEpoch();

      // 1. Check user's profile preferences
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .maybeSingle();

      if (!profileError && profile) {
        const prefs = profile.preferences as Record<string, unknown> | null;
        const value = prefs?.[preferenceKey] as T | undefined;
        if (value) {
          return value;
        }
      }

      // 2. Fall back to org-level setting
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', organizationId)
        .single();

      if (orgError) {
        if (!(await isSignedOutReadFailure(orgError, logoutEpoch))) {
          console.error(`Error fetching ${preferenceKey} settings:`, orgError);
        }
        return null;
      }

      const settings = org?.settings as Record<string, unknown> | null;
      return (settings?.[preferenceKey] as T) ?? null;
    },
    enabled: !!organizationId && !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return useMemo(() => data ?? defaultValue, [data, defaultValue]);
}

/**
 * Hook to update a single preference value in profiles.preferences.
 * Merges with existing preferences (does not overwrite other keys).
 */
export function useUpdateProfilePreference<T extends string>(
  organizationId: string | null | undefined,
  options: UseProfilePreferenceOptions<T>,
) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { preferenceKey, queryKeyPrefix, validValues } = options;

  return useMutation({
    mutationFn: async (newValue: T): Promise<void> => {
      if (!organizationId || !userId) {
        throw new Error('Organization ID and user ID are required');
      }

      // Runtime validation — defense-in-depth against DOM manipulation
      if (!validValues.has(newValue)) {
        throw new Error(`Invalid ${preferenceKey} value: ${newValue}`);
      }

      // Fetch current profile preferences
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id, preferences')
        .eq('user_id', userId)
        .eq('organization_id', organizationId)
        .maybeSingle();

      const currentPrefs = (existingProfile?.preferences as Record<string, unknown>) || {};
      const mergedPreferences = { ...currentPrefs, [preferenceKey]: newValue };

      if (existingProfile) {
        const { error } = await supabase
          .from('profiles')
          .update({
            preferences: mergedPreferences,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingProfile.id);

        if (error) {
          throw error;
        }
      } else {
        const { error } = await supabase
          .from('profiles')
          .insert({
            user_id: userId,
            organization_id: organizationId,
            preferences: mergedPreferences,
            updated_at: new Date().toISOString(),
          })
          .select('id')
          .single();

        if (error) {
          throw error;
        }
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [queryKeyPrefix, userId, organizationId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['current-org-profile', userId, organizationId],
      });
    },
  });
}
